import { NSARouterMakerResult } from 'namirasoft-account-react';
import { ProductFullRow } from 'namirasoft-api-product';
import { NSBoxEntity, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { CategoryRow, TicketInputRow, TicketPriority, TicketRow } from 'namirasoft-support';
import { Component, createRef } from 'react';
import { SupportEntities } from '../SupportEntities';
import { SupportLayout } from '../SupportLayout';
import { SupportProps } from '../SupportProps';
import TicketMessages from '../components/TicketMessages';

export interface TicketEditPageProps extends NSARouterMakerResult, SupportProps
{
    isEdit: boolean;
}

interface TicketEditPageState { }

export default class TicketEditPage extends Component<TicketEditPageProps, TicketEditPageState>
{
    private NSBoxString_Title = createRef<NSBoxString>();
    private NSBoxEntity_ProductID = createRef<NSBoxEntity<ProductFullRow>>();
    private NSBoxEntity_CategoryID = createRef<NSBoxEntity<CategoryRow>>();
    private NSBoxEnum_Priority = createRef<NSBoxEnum<TicketPriority>>();
    private NSBoxTextArea_Description = createRef<NSBoxTextArea>();
    private NSBoxString_ResourceID = createRef<NSBoxString>();

    constructor(props: TicketEditPageProps)
    {
        super(props);
        this.state = {};
    }

    override render()
    {
        return (
            <SupportLayout<TicketRow, TicketInputRow>
                action={{
                    description: "This section allows you to submit a new support request for an issue or question you are experiencing. To address your request effectively, it is recommended that the details be provided clearly and completely at the time of submission, as tickets are reviewed based on the information entered.",
                }}
                {...this.props}
                pages={{
                    entity: SupportEntities.ticket(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: () =>
                                    {
                                        let title: string = this.NSBoxString_Title.current?.getValue() ?? "";
                                        let product_id = this.NSBoxEntity_ProductID.current?.getValueOne() ?? "";
                                        let resource_id = this.NSBoxString_ResourceID.current?.getValue() ?? "";
                                        let category_id = this.NSBoxEntity_CategoryID.current?.getValueOne() ?? "";
                                        let priority: TicketPriority = this.NSBoxEnum_Priority.current?.getValueEnumOne(TicketPriority, TicketPriority.Medium) ?? TicketPriority.Medium;
                                        let description: string = this.NSBoxTextArea_Description.current?.getValue() ?? "";

                                        return {
                                            title,
                                            product_id,
                                            resource_id,
                                            category_id,
                                            priority,
                                            description,
                                        };
                                    },
                                    setRow: (row) =>
                                    {
                                        this.NSBoxString_Title.current?.setValue(row.title);
                                        this.NSBoxEntity_ProductID.current?.setValue(row.product_id);
                                        this.NSBoxString_ResourceID.current?.setValue(row.resource_id);
                                        this.NSBoxEntity_CategoryID.current?.setValue(row.category_id.toString());
                                        this.NSBoxEnum_Priority.current?.setValue(row.priority);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);
                                    },
                                },
                                ui: {}
                            },
                            getChildrenAfter: (_, row) => row && <TicketMessages row={row} />
                        },
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Title}
                        title="Title"
                        required
                        style={{ width: "100%" }}
                        info={{ link: "https://namirasoft.com/support/console-guide/ticket/#field-title" }}
                    />
                    <NSRow>
                        <NSBoxEntity<ProductFullRow>
                            ref={this.NSBoxEntity_ProductID}
                            title="Product"
                            required
                            table_name="product"
                            list={async (filters, page, size, sorts, _all_workspaces) =>
                            {
                                return await this.props.servers.api_product.product.List(filters, page, size, sorts);
                            }}
                            multiple={false}
                            getValue={item => item.id}
                            getTitle={item => item.name}
                            getSort={() => null}
                            info={{ link: "https://namirasoft.com/support/console-guide/ticket/#field-product" }}
                        />
                        <NSBoxString
                            ref={this.NSBoxString_ResourceID}
                            title="Resource ID"
                            required={false}
                            info={{ link: "https://namirasoft.com/support/console-guide/ticket/#field-resource-id" }}
                        />
                    </NSRow>
                    <NSRow>
                        <NSBoxEntity<CategoryRow>
                            ref={this.NSBoxEntity_CategoryID}
                            title={"Category"}
                            required
                            multiple={false}
                            table_name="category"
                            list={(filters, page, size, sorts) => this.props.servers.support.category.List(filters, page, size, sorts, null)}
                            getTitle={(item) => item.name}
                            getValue={(item) => item.id.toString()}
                            getSort={() => null}
                            info={{ link: "https://namirasoft.com/support/console-guide/ticket/#field-category" }}
                        />
                        <NSBoxEnum<TicketPriority>
                            ref={this.NSBoxEnum_Priority}
                            title="Priority"
                            required
                            multiple={false}
                            getEnumObject={() => TicketPriority}
                            info={{ link: "https://namirasoft.com/support/console-guide/ticket/#field-priority" }}
                        />
                    </NSRow>
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={true}
                        style={{ width: "100%" }}
                        info={{ link: "https://namirasoft.com/support/console-guide/ticket/#field-description" }}
                    />
                </NSColumn>
            </SupportLayout>
        );
    }
}